import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Badge from "@mui/material/Badge";
import Modal from "@mui/material/Modal";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDAlert from "components/MDAlert";
import {
  Tooltip,
  IconButton,
  CardContent,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  DialogActions,
  ListItem,
  ListItemText,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import CrudService from "services/cruds-service";
import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";
import { useNavigate } from "react-router-dom";
import MDButton from "components/MDButton";
import { toast } from "react-toastify";
import { TablePaginationActions } from "examples/Tables/CustomPaginationTable";

function CampaignCompanyList() {
  let { state } = useLocation();
  const ability = useAbility(AbilityContext);
  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [notification, setNotification] = useState({
    value: false,
    text: "",
  });
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState([]);
  const [campaignModelOpen, setCampaignModelOpen] = useState(false);
  const [campaign, setCampaign] = useState([]);
  const [campaignTableData, setCampaignTableData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [companyCampaign, setCompanyCampaign] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    (async () => {
      try {
        setLoader(true);
        const response = await CrudService.getAllCampaignCompany(page, pageSize);
        setData(response.data);
        if(response.pagination){
          setTotal(response.pagination.total)
        }
        setLoader(false);
      } catch (err) {
        console.error("err", err);
        setLoader(false);
      }
    })();
    document.title = `Rivio | Campaign | Company`;
  }, []);

  useEffect(() => {
    async function fetchCampaign() {
      const campaignRes = await CrudService.getUniqueCampaigns();

      setCampaign(campaignRes.data);
    }
    fetchCampaign();
  }, []);

  async function onPageChange(e, page) {
    e.stopPropagation();
    setLoader(true);
    const response = await CrudService.getAllCampaignCompany(page, pageSize);
    if(response.pagination){
      setPage(response.pagination.page)
      setPageSize(response.pagination.pageSize)
      setTotal(response.pagination.total)
    }
    setData(response.data);
    setLoader(false);
  }

  useEffect(() => {
    if (!state) return;
    setNotification({
      value: state.value,
      text: state.text,
    });
  }, [state]);

  useEffect(() => {
    setTableData(getRows(data));
  }, [data]);

  useEffect(() => {
    setCampaignTableData(getCampaignRows(campaign));
  }, [campaign]);

  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({
          value: false,
          text: "",
        });
      }, 5000);
    }
  }, [notification]);

  const clickAddHandler = () => {
    navigate("/campaign/company/new");
  };

  const clickEditHandler = async (id) => {
    navigate(`/campaign/company/edit/${id}`);
  };

  const clickDeleteHandler = async (e, id) => {
    try {
      if (!confirm("Are you sure you want to delete this Company?")) {
        e.nativeEvent.stopImmediatePropagation();
      } else {
        setLoader(true);
        await CrudService.removeCampaignCompany(id);
        // the delete does not send a response
        // so I need to get again the categories to set it and this way the table gets updated -> it goes to the useEffect with data dependecy
        const response = await CrudService.getAllCampaignCompany();
        setData(response.data);
        setNotification({
          value: true,
          text: "The Company has been successfully deleted",
        });
        setLoader(false);
      }
    } catch (err) {
      // it sends error is the API is associated with an API
      console.error(err);
      if (err.hasOwnProperty("msg")) {
        setNotification({
          value: true,
          text: err.msg,
        });
      }
      setLoader(false);
      return null;
    }
  };

  const handleSaveCampaignAssociation = async () => {
    const data = {
      company_ids: selectedRows,
      campaign_id: selectedCampaign,
    };
    try {
      await CrudService.createCampaignAssociation(data);
      setCampaignModelOpen(false);
      setSelectedRows([]);
      setSelectedCampaign("");
      toast.info("The association was successfully created");
    } catch (err) {
      console.error(err);
    }
  };

  const getRows = (info) => {
    let updatedInfo = info.map((row) => {
      return {
        id: row.company_id,
        external_row_id: row.external_row_id,
        name: row.company_name,
        phone: row.company_phone,
        email: row.company_email,
        url: row.company_url,
        category: row.company_category,
        street_address1: row.primary_street_address1,
        street_address2: row.primary_street_address2,
        city: row.primary_city,
        state: row.primary_state,
        country: row.primary_country,
        postal_code: row.primary_postal_code,
        CampaignAssociations: row.CampaignAssociations,
      };
    });
    return updatedInfo;
  };

  const dataTableData = {
    columns: [
      { Header: "ID", accessor: "id" },
      {
        Header: "Campaign Count", accessor: "campaign_count",
        Cell: (info) => {
          return info.cell.row.original.CampaignAssociations.length
        }
      },
      // { Header: 'Row ID', accessor: 'external_row_id' },
      {
        Header: "name",
        accessor: "name",
        Cell: (info) => {
          return info.cell.row.original.name ? (
            <span
              style={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                flexGrow: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setCompanyCampaign(info.cell.row.original.CampaignAssociations);
                setCampaignDialogOpen(true);
              }}
            >
              {info.cell.row.original.name}
            </span>
          ) : (
            <></>
          );
        },
      },
      { Header: "Phone", accessor: "phone" },
      { Header: "email", accessor: "email" },
      { Header: "url", accessor: "url" },
      { Header: "category", accessor: "category" },
      { Header: "Street Address 1", accessor: "street_address1" },
      { Header: "Street Address 2", accessor: "street_address2" },
      { Header: "city", accessor: "city" },
      { Header: "state", accessor: "state" },
      // { Header: 'country', accessor: 'country' },
      { Header: "postal code", accessor: "postal_code" },
      {
        Header: "actions",
        disableSortBy: true,
        accessor: "",
        Cell: (info) => {
          return (
            <MDBox display="flex" alignItems="center">
              {ability.can("edit", "campaign") && (
                <Tooltip title="Edit Campaign">
                  <IconButton
                    onClick={() => clickEditHandler(info.cell.row.original.id)}
                  >
                    <MDTypography>
                      <EditIcon />
                    </MDTypography>
                  </IconButton>
                </Tooltip>
              )}

              {ability.can("delete", "campaign") && (
                <Tooltip title="Delete Campaign">
                  <IconButton
                    onClick={(e) =>
                      clickDeleteHandler(e, info.cell.row.original.id)
                    }
                  >
                    <MDTypography>
                      <DeleteIcon />
                    </MDTypography>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
    ],

    rows: tableData,
  };

  const getCampaignRows = (info) => {
    let updatedInfo = info.map((row) => {
      let color = row?.status_type_id === 1 ? "success" : "error";
      let variant = "contained";
      let badgeContent = row?.StatusType?.status_name;

      return {
        type: "parameters",
        id: row.campaign_id,
        name: row.name,
        description: row.description,
        status: (
          <Badge
            color={color}
            variant={variant}
            badgeContent={badgeContent}
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
              marginLeft: "20px",
            }}
          ></Badge>
        ),
      };
    });
    return updatedInfo;
  };

  const dataCampaignTableData = {
    columns: [
      { Header: "ID", accessor: "id", width: "10%" },
      { Header: "name", accessor: "name", width: "30%" },
      { Header: "Description", accessor: "description", width: "40%" },
      { Header: "Status", accessor: "status", width: "20%" },
    ],

    rows: campaignTableData,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notification.value && (
        <MDAlert color="info" my="20px">
          <MDTypography variant="body2" color="white">
            {notification.text || ""}
          </MDTypography>
        </MDAlert>
      )}
      <MDBox pt={1}>
        <MDBox>
          <Card>
            <MDBox
              p={3}
              lineHeight={1}
              display="flex"
              justifyContent="space-between"
            >
              <MDTypography variant="h5" fontWeight="medium">
                Campaign Company
              </MDTypography>
              <MDBox display="flex" gap={2}>
                {selectedRows.length > 0 && (
                  <MDButton
                    variant={"gradient"}
                    color={"dark"}
                    size="small"
                    onClick={() => setCampaignModelOpen(true)}
                  >
                    Create Association
                  </MDButton>
                )}
                {ability.can("create", "campaign") && (
                  <MDButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    type="submit"
                    onClick={clickAddHandler}
                  >
                    + Add Company
                  </MDButton>
                )}
              </MDBox>
            </MDBox>

            <DataTable
              table={dataTableData}
              canSearch={true}
              tableHeight="calc(100vh - 220px)"
              loader={loader}
              isSelection={true}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              entriesPerPage={{ defaultValue: 100 }}
              canPagination={false}
            />
            <TablePaginationActions count={total} page={page} rowsPerPage={pageSize} onPageChange={onPageChange} />
            {/* <MultiSelectDataTable rowData={getRows(data)} selectedRows={selectedRows} setSelectedRows={setSelectedRows}/> */}
          </Card>

          <Modal
            open={campaignModelOpen}
            onClose={() => setCampaignModelOpen(false)}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <Card
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "60%",
                backgroundColor: "white",
                p: 4,
                height: "60vh",
                overflow: "auto",
              }}
            >
              <DataTable
                table={dataCampaignTableData}
                fixedWidth={true}
                selectedRows={selectedCampaign.toString()}
                setSelectedRows={setSelectedCampaign}
                isSelection={true}
                canSearch={true}
                isMultiSelect={false}
                onRowRedirect={(row) => {
                  if (selectedCampaign === row) {
                    setSelectedCampaign("");
                  } else {
                    setSelectedCampaign(row);
                  }
                }}
              />
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  borderTop: "1px solid #ccc",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSaveCampaignAssociation}
                >
                  Save
                </Button>
              </Box>
            </Card>
          </Modal>
          <Dialog
            open={campaignDialogOpen}
            onClose={() => setCampaignDialogOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <MDTypography variant="h5" fontWeight="medium">
                Associated Campaigns
              </MDTypography>
            </DialogTitle>
            <DialogContent dividers>
              {companyCampaign.length === 0 ? (
                <MDTypography
                  variant="body1"
                  color="textSecondary"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  No campaigns available
                </MDTypography>
              ) : (
                <List>
                  {companyCampaign.map((campaign) => (
                    <ListItem
                      key={campaign.campaign_id}
                      sx={{
                        padding: "4px 16px",
                        borderBottom: "1px solid #eee",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <div style={{ display: 'flex', gap: '0.5rem' }}>

                            <MDTypography variant="body1" fontWeight="small">
                              {campaign.Campaign.campaign_id}
                            </MDTypography> |
                            <MDTypography variant="body1" fontWeight="small">
                              {campaign.Campaign.name}
                            </MDTypography> |
                            <MDTypography variant="body1" fontWeight="small">
                              {campaign.Campaign.StatusType?.status_name}
                            </MDTypography>
                          </div>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCampaignDialogOpen(false)}
                variant="contained"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CampaignCompanyList;
